import { CouponApplyPhase } from "../coupon-qualifiers/interfaces/coupon-qualifier.interface";
import { ExpiryCheckQualifier } from "../coupon-qualifiers/expiry-check";
import { OrderTypeQualifier } from "../coupon-qualifiers/order-type";
import { MinimumOrderAmountQualifier } from "../coupon-qualifiers/minimum-order-amount";
import { CouponLimitQualifier } from "../coupon-qualifiers/coupon-limit";
import { Util } from "../../../util/util";
import { BaseTriggerItemsQualifier } from "../coupon-qualifiers/trigger-items/base-trigger-items";
import { StoreLimitQualifier } from "../coupon-qualifiers/store-limit";
import { AfterTaxProportionalDistributor } from "../../..//domain/services/discount-distributors/after-tax-proportional-distributor";
import { BeforeTaxDistributor } from "../../..//domain/services/discount-distributors/before-tax-distributor";
import { ProportionalAllocator } from "../../..//domain/services/discount-distributors/allocators/proportional-allocator";
import { ProfileRequiredQualifier } from "../coupon-qualifiers/profile-required";
import { FundraiserQualifier } from "../coupon-qualifiers/fundraiser";
export class CouponBase {
    _data;
    _coupon;
    _dependencies;
    _identity;
    _preQualifiers;
    _postQualifiers;
    constructor(appType, sourceData, dependencies, data, identity) {
        this._data = sourceData;
        this._dependencies = dependencies;
        this._coupon = data;
        this._identity = identity;
        //add pre qualifiers here to check for store-level flags/settings, customer groups, etc.
        //these qualifiers are shared across all coupons and are in this specific order
        this._preQualifiers = [
            new ExpiryCheckQualifier(this._dependencies, this._data),
            new ProfileRequiredQualifier(this._data.isLoggedIn),
            new StoreLimitQualifier(appType, this._data),
            new OrderTypeQualifier(),
            new MinimumOrderAmountQualifier(CouponApplyPhase.PreApply),
            new CouponLimitQualifier(),
            new FundraiserQualifier(this._data)
        ];
        //add post qualifiers here to check for anything after a coupon was applied to make sure it
        //is still valid, such as minimum order amount, etc.
        this._postQualifiers = [
            new MinimumOrderAmountQualifier(CouponApplyPhase.PostApply),
        ];
    }
    get couponId() {
        return this._coupon.couponId;
    }
    get getTriggerItems() {
        const itemQualifier = Util.GetQualifier(this, BaseTriggerItemsQualifier);
        return itemQualifier ? itemQualifier.qualifiedItems : [];
    }
    get allowOtherCoupons() {
        return this._coupon.allowOtherCoupons;
    }
    get isReward() {
        return this._identity ? this._identity.isReward : false;
    }
    get isLoyaltyExcluded() {
        return this._coupon.excludeLoyaltyUsage ?? false;
    }
    getProperty(property) {
        return this._coupon[property];
    }
    getQualifiedDiscountItems(cart) {
        const itemQualifier = Util.GetQualifier(this, BaseTriggerItemsQualifier);
        if (!itemQualifier)
            return [];
        const result = itemQualifier.qualify(this._coupon, cart);
        if (!result.isValid) {
            return [];
        }
        return this.getDiscountItems(cart);
    }
    meetsPreConditions(cart) {
        return this._processQualifiers(this._preQualifiers, cart);
    }
    meetsPostConditions(cart) {
        return this._processQualifiers(this._postQualifiers, cart);
    }
    _getDiscountItemsFromTriggers(cart) {
        const discountItems = [];
        for (const tItem of this.getTriggerItems) {
            const oItem = cart.items[tItem.index];
            if (!oItem) {
                continue; // Skip if the item is not found in the cart
            }
            // Mark this item quantity to be used for discounting
            if (oItem.halfCount > 1) {
                cart.markAllHalves(cart.items, oItem.itemKey);
            }
            else {
                oItem.discountedMarkedQuantity += 1; // Notice: This modifies the passed oItem directly.
            }
            discountItems.push(...this._buildDiscountItems(cart, tItem, oItem));
        }
        return discountItems;
    }
    /**
     * The discount items a set of trigger items would produce, priced exactly the way the apply path
     * above prices them but without marking anything in the cart.
     *
     * Bug 75837: a coupon whose qualifying items can't all reach its price point has to be able to
     * judge a candidate before it commits to it. Doing that off the coupon's own pricing keeps the
     * judgement and the discount in agreement; a second copy of the pricing rules would not stay
     * that way.
     */
    _previewDiscountItems(cart, triggerItems) {
        const discountItems = [];
        for (const tItem of triggerItems) {
            const oItem = cart.items[tItem.index];
            if (!oItem) {
                continue;
            }
            discountItems.push(...this._buildDiscountItems(cart, tItem, oItem));
        }
        return discountItems;
    }
    _buildDiscountItems(cart, tItem, oItem) {
        if (oItem.halfCount > 1) {
            return this._getDiscountItemsForAllHalves(cart, tItem, oItem);
        }
        const isBuyingItem = tItem.isBogoBuyingItem === true;
        return [{
                itemArrayIndex: tItem.index,
                cPrice: isBuyingItem ? 0 : this._priceCouponItem(oItem.id, oItem.sizeId, tItem.modifierCount ?? 0),
                oPrice: isBuyingItem ? 0 : oItem.price,
                itemKey: oItem.itemKey,
                halfIndex: oItem.halfIndex,
                halfCount: oItem.halfCount,
                itemRecId: oItem.itemRecId,
                addOn: tItem.addOnPrice ?? 0,
                beforeTaxDiscount: 0, // Initialize beforeTaxDiscount
                afterTaxDiscount: 0 // Initialize afterTaxDiscount
            }];
    }
    _getDiscountItemsForAllHalves(cart, tItem, oItem) {
        const halfCount = oItem.halfCount || 1;
        const groupItems = this._getGroupCouponItems(tItem);
        // ADO #82541: price each half from its OWN item, covered toppings and add-on rather than
        // cascading the first qualifying half's price/add-on to every half. Every portion resolves
        // its own coverage from the coupon's qualifying items, including the matched (trigger) one:
        // the qualifier's modifier decrement is a qualification mechanism, not a pricing one, so
        // taking the matched portion's coverage from the trigger would price whichever portion the
        // qualifier happened to land on differently from its identical siblings.
        const halfShares = cart.items
            .map((cItem, index) => ({ cItem, index }))
            .filter(({ cItem }) => cItem.itemKey === oItem.itemKey)
            .map(({ cItem, index }) => {
            const isMatchedHalf = index === tItem.index;
            const coverage = this._resolveHalfCoverage(cItem, groupItems);
            const fullCouponPrice = this._priceCouponItem(cItem.id, cItem.sizeId, coverage.modifierCount);
            const pricedModifiers = Util.GetItemPricedModifierCount(cItem, this._data.preModifiers);
            // Bugs 82915 / 84748: what this portion's uncovered toppings actually cost is the
            // amount they raise the item's MENU price by, so price this half twice — once with
            // all of its priced toppings and once with only its exempt ones — and let
            // _getUncoveredModifierShares combine the halves per Setting 22. The exempt pools
            // OVERLAP rather than stack (82915's stakeholder rule): a topping covered by EITHER
            // the coupon's Toppings Covered or the item's own free-modifier allowance is exempt,
            // so the larger of the two is used, never their sum.
            const priceRow = this._getItemPriceRow(cItem.id, cItem.sizeId);
            const exemptCount = Math.max(this._getItemFreeModifierCount(priceRow), coverage.modifierCount);
            return {
                cItem,
                index,
                isMatchedHalf,
                cShare: fullCouponPrice / halfCount,
                addOnShare: coverage.addOnPrice / halfCount,
                fullMenuPrice: this._getHalfMenuPrice(priceRow, pricedModifiers),
                exemptMenuPrice: this._getHalfMenuPrice(priceRow, Math.min(pricedModifiers, exemptCount)),
                upchargeShare: this._getUpchargeModifierPrice(cItem) / halfCount,
            };
        });
        const uncoveredShares = this._getUncoveredModifierShares(halfShares.map(h => ({ full: h.fullMenuPrice, exempt: h.exemptMenuPrice })), halfCount);
        // Preserve the whole item's coupon price/add-on/uncovered/upcharge totals by parking any
        // per-half rounding remainder on the matched half.
        const cRemainder = this._roundingRemainder(halfShares.map(h => h.cShare));
        const addOnRemainder = this._roundingRemainder(halfShares.map(h => h.addOnShare));
        const uncoveredRemainder = this._roundingRemainder(uncoveredShares);
        const upchargeRemainder = this._roundingRemainder(halfShares.map(h => h.upchargeShare));
        // When the split item fills the Buy One Get One buying position, every one of its
        // halves contributes no coupon price and no original price, exactly as a whole buying item does.
        const isBuyingItem = tItem.isBogoBuyingItem === true;
        return halfShares.map((h, i) => ({
            itemArrayIndex: h.index,
            cPrice: isBuyingItem ? 0 : this._halfShare(h.cShare, cRemainder, h.isMatchedHalf),
            oPrice: isBuyingItem ? 0 : h.cItem.price,
            itemKey: h.cItem.itemKey,
            halfIndex: h.cItem.halfIndex,
            halfCount: h.cItem.halfCount,
            itemRecId: h.cItem.itemRecId,
            addOn: this._halfShare(h.addOnShare, addOnRemainder, h.isMatchedHalf),
            uncoveredModifierPrice: this._halfShare(uncoveredShares[i], uncoveredRemainder, h.isMatchedHalf),
            upchargeModifierPrice: this._halfShare(h.upchargeShare, upchargeRemainder, h.isMatchedHalf),
            beforeTaxDiscount: 0, // Initialize beforeTaxDiscount
            afterTaxDiscount: 0 // Initialize afterTaxDiscount
        }));
    }
    /** One half's rounded share of a whole-item amount; the matched half also absorbs the rounding remainder. */
    _halfShare(share, remainder, isMatchedHalf) {
        return Util.Float2(Util.Float2(share) + (isMatchedHalf ? remainder : 0));
    }
    /**
     * Total price of this portion's modifiers that add to the item's price without being priced
     * modifiers — an upcharged crust and the like (Bug 82910). The host sets a modifier's price to
     * its per-modifier rate when it counts for pricing and adds any upcharge on top, so a modifier
     * that counts for neither carries the upcharge alone.
     *
     * A priced modifier that ALSO carries an upcharge amount is not included: its price mixes the
     * per-modifier rate with the upcharge and the two cannot be separated here. That combination is
     * excluded by the setup instructions on Bug 82910 and has no agreed expected behaviour.
     */
    _getUpchargeModifierPrice(cItem) {
        return cItem.modifiers.reduce((total, mod) => (mod.countForPricing || mod.countForPricingByItself)
            ? total
            : total + Util.Float2(mod.price ?? 0), 0);
    }
    /**
     * Resolves an item's price row for a size, matching how the rest of the coupon code looks a
     * price up. Returns null when the item or a price row for that size is missing.
     */
    _getItemPriceRow(itemId, sizeId) {
        const mItem = this._getItem(itemId, this._data.items);
        return mItem?.prices.find(price => price.sizeId === sizeId) ?? null;
    }
    /**
     * Bug 82915: the number of "free" priced modifiers an item's price setup grants (the Back
     * Office "Free Modifiers" allowance). This is not a discrete field — it is encoded as the gap
     * between the item's base price and its minimumPrice floor:
     * freeModCount = round((minimumPrice - price) / perModifierPrice). Modifiers within this count
     * are absorbed by the minimumPrice floor at regular pricing time and never raise the item
     * price, so a price-point coupon must not charge them back. Returns 0 when there is no
     * minimumPrice floor (no allowance possible, and it also avoids an undefined -> NaN in the
     * division) or no per-modifier price to divide by.
     */
    _getItemFreeModifierCount(iPrice) {
        if (!iPrice?.minimumPrice)
            return 0;
        const perModifierPrice = Util.Float2(iPrice.perModifierPrice);
        if (perModifierPrice <= 0)
            return 0;
        return Math.max(0, Math.round((Util.Float2(iPrice.minimumPrice) - Util.Float2(iPrice.price)) / perModifierPrice));
    }
    /**
     * Prices a single half of a split item at a given priced-modifier count, mirroring the host
     * apps' regular pricing (POS fOrder.js fGetItemMenuPriceAsync / AOLO3 GetItemMenuPrice): every
     * half is priced as a WHOLE pizza — base price plus the per-modifier charge — then clamped UP
     * to the minimumPrice floor and DOWN to the maximumPrice cap. The minimumPrice floor is what
     * makes the item's "Free Modifiers" allowance free; the maximumPrice cap is what makes toppings
     * past a price ceiling free.
     *
     * Upcharge modifiers are deliberately left out: regular pricing adds them to both the running
     * price and the floor, so they cancel out of the modifier delta this feeds, and the coupon
     * types preserve them separately via upchargeModifierPrice (Bug 82910).
     */
    _getHalfMenuPrice(iPrice, modifierCount) {
        if (!iPrice)
            return 0;
        let price = Util.Float2(Util.Float2(iPrice.price) + modifierCount * Util.Float2(iPrice.perModifierPrice));
        const minPrice = Util.Float2(iPrice.minimumPrice);
        if (minPrice && price < minPrice)
            price = minPrice;
        // Regular pricing compares against the ABSOLUTE maximumPrice but assigns the raw
        // (possibly negative) value.
        const maxPrice = Util.Float2(iPrice.maximumPrice);
        if (maxPrice && price > Math.abs(maxPrice))
            price = maxPrice;
        return price;
    }
    /**
     * Bugs 84748 / 84749: each portion's share of the priced-modifier charge a price-point coupon
     * must add back on top of its configured price — the exact amount the item's MENU price rises
     * because of priced toppings that neither the coupon's Toppings Covered nor the item's own
     * free-modifier allowance exempts.
     *
     * Derived from the menu price rather than from a topping count because the two do not scale
     * together on a split item. Regular pricing prices every half as a whole pizza and then
     * combines the halves per Setting 22: Highest Price (1) takes the MAX of the halves, otherwise
     * Average takes their sum / halfCount. So an extra topping on one half raises the item by
     * perModifierPrice / halfCount under Average but by the FULL perModifierPrice under Highest —
     * and under Highest a topping on the CHEAPER half can be swallowed entirely by the dearer
     * half's price and cost the customer nothing. The previous count-times-blended-rate model
     * (ADO #82541 G4) only matched the Average case with uniform rates.
     *
     * The whole-item charge-back is allocated across the portions in proportion to each half's own
     * menu-price rise, so a portion carrying no chargeable extras owes nothing; under Average this
     * reduces exactly to each half's own delta / halfCount. The shares sum to the whole-item
     * charge-back; the caller's rounding-remainder machinery keeps the rounded total faithful.
     */
    _getUncoveredModifierShares(halves, halfCount) {
        if (halves.length === 0)
            return [];
        const highestPrice = this._data.settings?.HHP === 1 /* HalfHalfPricing.Highest */;
        const combine = (prices) => highestPrice
            ? Math.max(...prices)
            : Util.Float2(prices.reduce((sum, p) => sum + p, 0) / halfCount);
        const chargeBack = Math.max(0, Util.Float2(combine(halves.map(h => h.full)) - combine(halves.map(h => h.exempt))));
        if (chargeBack === 0)
            return halves.map(() => 0);
        const deltas = halves.map(h => Math.max(0, h.full - h.exempt));
        const deltaTotal = deltas.reduce((sum, d) => sum + d, 0);
        if (deltaTotal <= 0) // unreachable when chargeBack > 0; guards a 0-division all the same
            return halves.map(() => chargeBack / halves.length);
        return deltas.map(d => (chargeBack * d) / deltaTotal);
    }
    /**
     * Restricts coupon-item lookups to the group the matched half belongs to, so a half's coverage
     * is resolved against the same qualifying-items slot it filled. The qualifier reports that group
     * on the trigger item; deriving it here by searching for a matching item/size/modifierCount
     * instead would pick the wrong group whenever the same item is listed in more than one, handing
     * siblings another group's Toppings Covered and Add-On Price. Falls back to the full item list
     * when the qualifier doesn't work in groups (e.g. category coupons, which carry no items).
     */
    _getGroupCouponItems(tItem) {
        const couponItems = this._coupon.items ?? [];
        if (tItem.groupIndex === undefined)
            return couponItems;
        return couponItems.filter(ci => ci.groupIndex === tItem.groupIndex);
    }
    /**
     * Resolves the coupon coverage (toppings covered + add-on price) for a single half from the
     * coupon's qualifying items. Prefers the row that covers the most toppings without exceeding the
     * half's actual toppings, mirroring the qualifier's exact-then-greater matching. Returns zero
     * coverage when the half matches no qualifying item (e.g. category coupons that don't target
     * specific items).
     *
     * A half carrying fewer toppings than every row configured for it still contributes that row's
     * configured Toppings Covered — per AC G2 a portion contributes "its own configured values", and
     * the guard against covering more than the customer is paying is the actual-price cap the coupon
     * types apply to the whole item (see the AC's note on G9), not a reduced coverage here. Reducing
     * it here as well would subtract the shortfall twice and under-discount the item.
     */
    _resolveHalfCoverage(half, groupItems) {
        const candidates = groupItems.filter(ci => ci.itemId === half.id && ci.sizeId === half.sizeId);
        if (candidates.length === 0) {
            return { modifierCount: 0, addOnPrice: 0 };
        }
        const halfModifiers = Util.GetItemPricedModifierCount(half, this._data.preModifiers);
        const atOrBelow = candidates.filter(ci => ci.modifierCount <= halfModifiers);
        // Best row the half satisfies; if it satisfies none, the least demanding row configured for it.
        // The comparisons are deliberately strict so that two rows configured at the same Toppings
        // Covered resolve to whichever the coupon lists first, taking its Add-On Price with it.
        const chosen = atOrBelow.length > 0
            ? atOrBelow.reduce((best, ci) => (ci.modifierCount > best.modifierCount ? ci : best))
            : candidates.reduce((best, ci) => (ci.modifierCount < best.modifierCount ? ci : best));
        // Coverage and add-on always come from the same row; they are never taken from different rows.
        return { modifierCount: chosen.modifierCount, addOnPrice: chosen.addOnPrice ?? 0 };
    }
    _roundingRemainder(shares) {
        const target = Util.Float2(shares.reduce((sum, s) => sum + s, 0));
        const rounded = Util.Float2(shares.reduce((sum, s) => sum + Util.Float2(s), 0));
        return Util.Float2(target - rounded);
    }
    _getTotalCouponItemsPrices(itemsToDiscount) {
        let couponItemPrice = 0;
        let orderItemPrice = 0;
        let couponAddOnPrice = 0;
        for (const item of itemsToDiscount) {
            couponItemPrice += Util.Float2(item.cPrice);
            orderItemPrice += Util.Float2(item.oPrice);
            couponAddOnPrice += Util.Float2(item.addOn);
        }
        return {
            couponItemPrice: Util.Float2(couponItemPrice),
            orderItemPrice: Util.Float2(orderItemPrice),
            couponAddOnPrice: Util.Float2(couponAddOnPrice),
        };
    }
    _adjustDiscountAmount(cart, discountAmount) {
        const maxDiscount = this._coupon.maximumDiscountAmount;
        if (maxDiscount > 0 && discountAmount > maxDiscount) {
            return maxDiscount;
        }
        const orderMaxDiscountAllowed = cart.subtotal - cart.discount + cart.orderTypeCharge;
        if (discountAmount > orderMaxDiscountAllowed) {
            return orderMaxDiscountAllowed;
        }
        return Util.Float2(discountAmount);
    }
    getAfterTaxDistributor() {
        return new AfterTaxProportionalDistributor();
    }
    getBeforeTaxDistributor() {
        return new BeforeTaxDistributor(new ProportionalAllocator());
    }
    _processQualifiers(qualifiers, cart) {
        for (const qualifier of qualifiers) {
            const result = qualifier.qualify(this._coupon, cart);
            if (!result.isValid) {
                return result; // short-circuit and return first failure
            }
        }
        return { isValid: true, errorCode: null };
    }
    _priceCouponItem(itemId, sizeId, modifierCount) {
        const mItem = this._getItem(itemId, this._data.items);
        if (!mItem)
            return 0;
        const matchingPrice = mItem.prices.find(price => price.sizeId === sizeId);
        if (!matchingPrice)
            return 0;
        let price = Util.Float2(matchingPrice.price) + (Util.Float2(matchingPrice.perModifierPrice) * modifierCount);
        price = Math.max(price, Util.Float2(matchingPrice.minimumPrice));
        return price;
    }
    _getItem(itemId, items) {
        for (const [index, item] of items.entries()) {
            if (item.itemId === itemId) {
                item.index = index;
                return item;
            }
        }
        return null;
    }
}
//# sourceMappingURL=coupon-base.js.map